import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowUp, Loader2, Plus, X } from "lucide-react";
import clsx from "clsx";
import type { Skill } from "@/api/assistant";

interface Props {
  /** The message text — controlled, so the page can keep unsent drafts. */
  text: string;
  onText: (text: string) => void;
  skills: Skill[];
  skill: Skill | null;
  onSkill: (s: Skill | null) => void;
  onSend: (text: string) => void;
  busy: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
}

const OUTPUT_LABEL: Record<Skill["output"], string> = {
  chat: "Answer",
  pdf: "PDF",
  research: "Research",
};

/** The message box: Enter sends, Shift+Enter breaks a line, "/" picks a skill. */
export default function Composer({
  text,
  onText: setText,
  skills,
  skill,
  onSkill,
  onSend,
  busy,
  disabled = false,
  autoFocus = false,
  placeholder = "Ask about any company or deal. Type / for skills",
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLTextAreaElement>(null);

  // "/par…" at the start of the box filters the skill menu.
  const slash = text.match(/^\/([\w-]*)$/);
  const query = slash ? slash[1].toLowerCase() : "";
  const showMenu = menuOpen || !!slash;
  const matches = useMemo(
    () =>
      skills.filter(
        (s) => !query || s.name.includes(query) || s.title.toLowerCase().includes(query),
      ),
    [skills, query],
  );

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    // Grow with the text, up to a limit.
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [text]);

  useEffect(() => setActive(0), [query, menuOpen]);

  const pick = (s: Skill) => {
    onSkill(s);
    setMenuOpen(false);
    if (slash) setText("");
    ref.current?.focus();
  };

  const submit = () => {
    if (busy || disabled) return;
    if (!text.trim() && !skill) return;
    onSend(text.trim());
    setText("");
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMenu && matches.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((a) => Math.min(a + 1, matches.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((a) => Math.max(a - 1, 0));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pick(matches[active]);
        return;
      }
    }
    if (e.key === "Escape") {
      setMenuOpen(false);
      if (slash) setText("");
      return;
    }
    if (e.key === "Backspace" && !text && skill) {
      onSkill(null);
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="relative">
      {showMenu && (
        <div
          role="listbox"
          aria-label="Skills"
          className="panel absolute bottom-full left-0 right-0 mb-2 p-1.5 z-20 max-h-[320px] overflow-y-auto animate-fade-up"
        >
          <div className="section-title px-3 pt-2 pb-1.5">Skills</div>
          {matches.length === 0 ? (
            <div className="px-3 py-3 text-sm text-mut-500">
              No skill matches “{query}”. Add one in the Library.
            </div>
          ) : (
            matches.map((s, i) => (
              <button
                key={s.id}
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(s)}
                className={clsx(
                  "w-full text-left px-3 py-2.5 rounded-xl flex items-start gap-3",
                  i === active && "bg-fg/[0.05]",
                )}
              >
                <span className="font-mono text-sm text-accent-violet pt-0.5 shrink-0">
                  /{s.name}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-md font-medium">{s.title}</span>
                  <span className="block text-sm text-mut-500 truncate">{s.description}</span>
                </span>
                <span className="chip-tint shrink-0" style={{ ["--c" as string]: "var(--azure)" }}>
                  {OUTPUT_LABEL[s.output]}
                </span>
              </button>
            ))
          )}
        </div>
      )}

      <div
        className={clsx(
          "rounded-[22px] border bg-ink-900 transition-shadow",
          "border-fg/[0.1] shadow-[0_1px_2px_rgb(var(--shadow)/0.04),0_16px_40px_rgb(var(--shadow)/0.08)]",
          "focus-within:border-accent-azure/40 focus-within:shadow-[0_0_0_4px_rgb(var(--azure)/0.10),0_16px_40px_rgb(var(--shadow)/0.08)]",
        )}
      >
        {skill && (
          <div className="px-4 pt-3">
            <span className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg bg-accent-violet/10 text-accent-violet text-sm font-medium">
              /{skill.name} · {skill.title}
              <button
                aria-label="Remove skill"
                className="p-0.5 rounded hover:bg-accent-violet/15"
                onClick={() => onSkill(null)}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          </div>
        )}
        <textarea
          ref={ref}
          id="assistant-input"
          rows={1}
          value={text}
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={skill ? `Which company or deal? e.g. “AVAX S.A.”` : placeholder}
          aria-label="Message the assistant"
          className="block w-full resize-none bg-transparent px-5 pt-4 pb-2 text-base text-mut-100 placeholder:text-mut-500 outline-none min-h-[56px] disabled:opacity-60"
        />
        <div className="flex items-center gap-2 px-3 pb-3">
          <button
            type="button"
            className="btn btn-ghost !px-2 !py-1.5 rounded-full"
            aria-label="Choose a skill"
            title="Skills"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <Plus className="w-4 h-4" />
          </button>
          <span className="text-sm text-mut-500 hidden sm:inline">
            Enter to send · Shift+Enter for a new line
          </span>
          <div className="flex-1" />
          <span className="text-sm text-mut-500 hidden sm:inline">Opus 5.5</span>
          {busy ? (
            <span
              className="grid place-items-center w-9 h-9 rounded-full bg-fg/[0.06] text-mut-300"
              role="status"
              aria-label="Answering"
            >
              <Loader2 className="w-4 h-4 animate-spin" />
            </span>
          ) : (
            <button
              type="button"
              disabled={disabled || (!text.trim() && !skill)}
              className="grid place-items-center w-9 h-9 rounded-full text-white disabled:opacity-35 transition-opacity"
              style={{ background: "var(--grad)" }}
              aria-label="Send"
              onClick={submit}
            >
              <ArrowUp className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
